import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-brand-primary text-text-primary/92 hover:bg-brand-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-border-subtle/8 bg-transparent hover:bg-bg-surface hover:text-text-primary/92',
        secondary:
          'bg-bg-surface text-text-primary/92 hover:bg-bg-surface/80',
        ghost:
          'hover:bg-bg-surface hover:text-text-primary/92',
        link:
          'text-text-primary/92 underline-offset-4 hover:underline',
        // CTA button - white background with spotlight glow on hover (contained within button width)
        cta:
          'bg-white text-brand-primary-deep font-medium hover:bg-white/95 hover:shadow-[0_12px_16px_-8px_rgba(255,255,255,0.5)] shadow-[0_0_0px_rgba(255,255,255,0)]',
      },
      size: {
        default: 'h-10 px-4 py-2 rounded-sm',
        sm: 'h-9 px-3 rounded-sm',
        lg: 'h-11 px-8 rounded-md',
        icon: 'h-10 w-10 rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
